import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { DynamicHomepageService } from "../../../services/dynamic-homepage.service";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ContactObject } from "../../utility/ContactObject";
import { HomepageSection } from "../homepage.component";
import {
  MediaType,
  MediaUpdateField,
  UpdateMediaRequest,
} from "../../utility/UpdateMediaRequest";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { LinkObject } from "../../utility/LinkObject";
import { HtmlTextObject } from "../../utility/HtmlTextObject";
import { ConfirmChangesDialogComponent } from "../confirm-changes-dialog/confirm-changes-dialog.component";
import { ErrorHandlerDialogComponent } from "../../error-handler-dialog/error-handler-dialog.component";

export interface CreateFormElement {
  description: string;
  displayOrder: number;
  displayText: string;
  id: string;
  sectionId: number;
  type: string;
  url: string;
}
@Component({
    selector: "app-homepage-editor",
    templateUrl: "./homepage-editor.component.html",
    styleUrls: ["./homepage-editor.component.scss"],
    standalone: false
})
export class HomepageEditorComponent implements OnInit {
  section: HomepageSection;
  dataSource: MatTableDataSource<any>;
  editMode: boolean;
  isInvalidDragEvent = false;
  @ViewChild("table") table: MatTable<any>;

  columnsToDisplay: string[] = [];
  linkCols: string[] = ["displayText", "url", "description"];
  contactCols: string[] = ["name", "title", "email", "url"];
  htmlTextCols: string[] = ["description"];
  forms: Map<string, UntypedFormGroup>;
  order: Map<string, number>;

  pendingCreateObjects: any[];
  pendingDeleteObjects: string[];

  contactForm: UntypedFormGroup = this.fb.group({
    name: ["", Validators.required],
    title: ["", Validators.required],
    email: ["", Validators.email],
    url: [""],
  });

  linkForm: UntypedFormGroup = this.fb.group({
    displayText: ["", Validators.required],
    url: [""],
    description: [""],
  });

  htmlTextForm: UntypedFormGroup = this.fb.group({
    description: ["", Validators.required],
  });

  constructor(
    private dialogRef: MatDialogRef<HomepageEditorComponent>,
    private dialog: MatDialog,
    private changeDetectorRefs: ChangeDetectorRef,
    private homepageService: DynamicHomepageService,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) obj: any
  ) {
    this.section = obj.section;
    this.dataSource = new MatTableDataSource();
    this.editMode = false;
    this.isInvalidDragEvent = true;
    this.forms = new Map<string, UntypedFormGroup>();
    this.order = new Map<string, number>();

    this.pendingCreateObjects = [];
    this.pendingDeleteObjects = [];
  }

  ngOnInit(): void {
    if (this.section.sectionType === MediaType.contact) {
      this.columnsToDisplay = this.contactCols;
    } else if (this.section.sectionType === MediaType.link) {
      this.columnsToDisplay = this.linkCols;
    } else {
      this.columnsToDisplay = this.htmlTextCols;
    }
    this.refresh();
  }

  refresh() {
    this.homepageService
      .getMediaForSection(this.section.sectionId)
      .subscribe((res) => {
        if (res) {
          if (res.links && res.links.length > 0) {
            this.dataSource.data = res.links;
          } else if (res.contacts && res.contacts.length > 0) {
            this.dataSource.data = res.contacts;
          } else if (res.htmlTexts && res.htmlTexts.length > 0) {
            this.dataSource.data = res.htmlTexts;
          } else {
            this.dataSource.data = [];
          }
        } else {
          this.dataSource.data = [];
        }
        this.resetTableColumns();
        this.dataSource = new MatTableDataSource(this.dataSource.data);
        this.changeDetectorRefs.detectChanges();
      });
  }

  getHeader(column: string) {
    if (column === "displayText") {
      return "DISPLAY TEXT";
    } else if (column === "url" && this.section.sectionType === "contact") {
      return "IMAGE SOURCE";
    } else if (
      column === "description" &&
      this.section.sectionType === "htmlText"
    ) {
      return "TEXT";
    } else {
      return column.toUpperCase();
    }
  }

  deleteMedia(e) {
    if (this.pendingCreateObjects.includes(e)) {
      // If deleting an object that hasn't been saved yet, just pop from pendingCreateObjects
      const index = this.pendingCreateObjects.indexOf(e);
      if (index > -1) {
        this.pendingCreateObjects.splice(index, 1);
      }
    } else if (!this.pendingDeleteObjects.includes(e)) {
      this.pendingDeleteObjects.push(e.id);
    } else {
    }
    this.dataSource.data.splice(this.dataSource.data.indexOf(e), 1);
    this.dataSource._updateChangeSubscription();
    this.changeDetectorRefs.detectChanges();
  }

  closeEditor() {
    this.columnsToDisplay.shift();
    this.columnsToDisplay.shift();
    this.dialogRef.close();
  }

  onSubmit() {
    this.openConfirmationDialog();
  }

  // convert string to a MediaUpdateField key
  getEnum(k: string): MediaUpdateField {
    return MediaUpdateField[k as keyof typeof MediaUpdateField];
  }

  // dynamically create FormGroup to display media details in a row
  createForm(e: CreateFormElement) {
    const form = new UntypedFormGroup({});
    for (const col of this.columnsToDisplay) {
      if (col !== " " && col !== "order") {
        if (col === "email") {
          form.addControl(
            col,
            new UntypedFormControl(e[col], Validators.email)
          );
        } else {
          form.addControl(col, new UntypedFormControl(e[col]));
        }
      }
    }
    const index = this.dataSource.data.indexOf(e);
    this.forms.set(e.id, form);
    this.order.set(e.id, index);
  }

  getFormGroup(mediaId): UntypedFormGroup {
    return this.forms.get(mediaId);
  }

  // handles drag and drop reordering
  dropTable(event: CdkDragDrop<MatTableDataSource<any>>) {
    const prevIndex = this.dataSource.data.findIndex(
      (d) => d === event.item.data
    );
    moveItemInArray(this.dataSource.data, prevIndex, event.currentIndex);

    // Save order of items that were switched:
    this.order.set(event.item.data.id, event.currentIndex);
    this.order.set(this.dataSource.data[prevIndex].id, prevIndex);
    this.table.renderRows();
  }

  addContact() {
    const contact: ContactObject = {
      url: this.contactForm.get("url").value,
      sectionId: this.section.sectionId.toString(),
      displayOrder: this.dataSource.data
        ? this.dataSource.data.length.toString()
        : "0",
      name: this.contactForm.get("name").value,
      title: this.contactForm.get("title").value,
      email: this.contactForm.get("email").value,
    };
    this.addToTable(contact);
    this.contactForm.reset();
  }

  addLink() {
    const link: LinkObject = {
      url: this.linkForm.get("url")?.value,
      sectionId: this.section.sectionId.toString(),
      displayOrder: this.dataSource.data
        ? this.dataSource.data.length.toString()
        : "0",
      displayText: this.linkForm.get("displayText").value,
      description: this.linkForm.get("description").value,
    };
    this.addToTable(link);
    this.linkForm.reset();
  }

  addHtmlText() {
    const htmlText: HtmlTextObject = {
      url: this.htmlTextForm.get("url")?.value,
      sectionId: this.section.sectionId.toString(),
      displayOrder: this.dataSource.data
        ? this.dataSource.data.length.toString()
        : "0",
      description: this.htmlTextForm.get("description").value,
    };
    this.addToTable(htmlText);
    this.htmlTextForm.reset();
  }

  addToTable(e) {
    this.pendingCreateObjects.push(e);
    if (this.dataSource.data) {
      this.dataSource.data.push(e);
    } else {
      this.dataSource = new MatTableDataSource();
      this.dataSource.data.push(e);
    }
    this.dataSource._updateChangeSubscription();
  }

  openConfirmationDialog() {
    const dialogRef = this.dialog.open(ConfirmChangesDialogComponent, {
      disableClose: true,
      data: {
        sectionType: this.section.sectionType,
        columnsToDisplay: this.columnsToDisplay,
        toDelete: this.pendingDeleteObjects,
        toCreate: this.pendingCreateObjects,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      if (result.save === true) {
        this.forms.forEach((form: UntypedFormGroup, id: string) => {
          if (id) {
            const updatedFields: Map<MediaUpdateField, string> = new Map<
              MediaUpdateField,
              string
            >();

            Object.keys(form.controls).forEach((key) => {
              let enumName = key;
              if (key !== "displayOrder" && key !== "displayText") {
                enumName =
                  "media" +
                  key.charAt(0).toUpperCase() +
                  key.slice(1).toLowerCase();
              }
              if (form.controls[key] != null) {
                updatedFields.set(
                  this.getEnum(enumName),
                  form.controls[key].value?.trim()
                );
              }
            });
            updatedFields.set(
              MediaUpdateField.displayOrder,
              this.order.get(id).toString()
            );

            const req: UpdateMediaRequest = new UpdateMediaRequest(
              id,
              this.section.sectionType,
              updatedFields
            );
            this.homepageService.updateMedia(req).subscribe({
              next: () => {},
              error: (_) => {
                this.dialog.open(ErrorHandlerDialogComponent, {
                  data: {
                    message:
                      "There was an error updating the item. Please try again.",
                  },
                });
              },
            });
          }
        });
        this.closeEditor();
      } else {
        this.pendingDeleteObjects = result.newDeleteList;

        // Return all elements that were restored to the table
        for (const e of result.toRestore) {
          this.dataSource.data.push(e);
          const index = this.pendingDeleteObjects.indexOf(e);
          if (index > -1) {
            this.pendingDeleteObjects.splice(index, 1);
          }
        }

        // Update and reset table view
        this.dataSource._updateChangeSubscription();
        this.columnsToDisplay.shift();
        this.resetTableColumns();
      }
    });
  }

  resetTableColumns() {
    this.columnsToDisplay.unshift("order");
    this.columnsToDisplay.unshift(" ");
  }
}
